// Virtual waiting room — shown when the system is at capacity. Holds the visitor
// calmly until a slot frees, then advances them into booking. Editorial-luxury:
// near-black canvas, one red accent used sparingly, a single hero (the queue
// position), physical spring motion, no layout shift. Mirrors the app's voice.

import {
  AnimatePresence,
  motion,
  useMotionValueEvent,
  useReducedMotion,
  useSpring,
} from 'framer-motion';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueueStatus } from './api/useQueueStatus.ts';

const nf = new Intl.NumberFormat('tr-TR');

function formatEta(seconds: number): string {
  if (seconds >= 90) return `~${Math.round(seconds / 60)} dakika`;
  if (seconds >= 45) return '~1 dakika';
  return `~${Math.max(5, Math.round(seconds / 5) * 5)} saniye`;
}

type Props = {
  /** Called once the visitor reaches the front and is admitted into booking. */
  onAdmit?: () => void;
};

export function WaitingRoom({ onAdmit }: Props) {
  const q = useQueueStatus();
  const reduce = useReducedMotion();

  // Smoothly count the hero number toward the live position. Tabular figures +
  // a fixed-center layout keep it from shifting as digits change.
  const spring = useSpring(q.initialPosition, { stiffness: 55, damping: 22 });
  const [shown, setShown] = useState(q.initialPosition);
  useMotionValueEvent(spring, 'change', (v) => setShown(Math.round(v)));
  useEffect(() => {
    if (reduce) setShown(q.position);
    else spring.set(q.position);
  }, [q.position, reduce, spring]);

  // Hand off shortly after admission so the success beat is felt, not skipped.
  useEffect(() => {
    if (q.phase !== 'admitted' || !onAdmit) return;
    const t = setTimeout(onAdmit, 1600);
    return () => clearTimeout(t);
  }, [q.phase, onAdmit]);

  const admitted = q.phase === 'admitted';
  const almost = q.phase === 'almost';
  const connecting = q.phase === 'connecting';

  const ease = reduce ? { duration: 0 } : { type: 'spring' as const, stiffness: 60, damping: 18 };
  const rise = reduce
    ? {}
    : {
        initial: { opacity: 0, y: 14 },
        animate: { opacity: 1, y: 0 },
      };

  return (
    <div className="relative flex min-h-dvh flex-col overflow-hidden bg-bg-deep text-text">
      {/* Atmosphere: a single soft vignette for depth — not a gradient wash. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(120% 80% at 50% 38%, rgba(215,42,45,0.05), transparent 55%), radial-gradient(140% 120% at 50% 100%, #050504, #0a0907 60%)',
        }}
      />

      {/* Top rule: brand + live pulse */}
      <header className="relative z-10 flex items-center justify-between px-8 py-6">
        <span className="kicker text-text-faint">Acar Sanat · Rezervasyon</span>
        <LivePulse reduce={!!reduce} dim={connecting} />
      </header>

      {/* Monument */}
      <main className="relative z-10 flex flex-1 items-center justify-center px-6">
        <motion.div
          className="w-full max-w-[560px] text-center"
          initial={reduce ? false : 'hidden'}
          animate="show"
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.09 } } }}
        >
          <AnimatePresence mode="wait">
            {admitted ? (
              <motion.div
                key="admitted"
                {...rise}
                exit={reduce ? undefined : { opacity: 0, y: -10 }}
                transition={ease}
              >
                <div className="kicker mb-4 text-accent">Sıra Sizde</div>
                <h1 className="display text-[clamp(34px,6vw,64px)] font-light leading-[1.05] tracking-tight">
                  Yönlendiriliyorsunuz
                </h1>
                <p className="mt-5 text-[14px] text-text-muted">
                  Koltuk seçimine alınıyorsunuz — bir saniye.
                </p>
              </motion.div>
            ) : (
              <motion.div key="queue">
                <motion.div variants={itemVariants(reduce)}>
                  <div className="kicker mb-6 text-text-faint">
                    {connecting ? 'Bağlanılıyor' : 'Sıradaki Yeriniz'}
                  </div>
                </motion.div>

                {/* Hero number — the only loud thing on the page */}
                <motion.div
                  variants={itemVariants(reduce)}
                  className="display tnum mx-auto font-light leading-none tracking-tight transition-colors duration-500"
                  style={{
                    fontSize: 'clamp(72px, 17vw, 184px)',
                    color: almost ? 'var(--accent)' : 'var(--text)',
                  }}
                >
                  {connecting ? '—' : nf.format(shown)}
                </motion.div>

                <motion.div
                  variants={itemVariants(reduce)}
                  className="mt-6 text-[13px] tracking-wide text-text-muted tnum"
                >
                  {connecting ? (
                    'Yoğunluk kontrol ediliyor…'
                  ) : (
                    <>
                      Önünüzde <span className="text-text">{nf.format(q.peopleAhead)}</span> kişi
                      <span className="mx-2.5 text-text-faint">·</span>
                      Tahmini <span className="text-text">{formatEta(q.etaSeconds)}</span>
                    </>
                  )}
                </motion.div>

                <motion.div variants={itemVariants(reduce)} className="mt-10">
                  <ProgressRule progress={q.progress} reduce={!!reduce} active={almost} />
                </motion.div>

                <motion.p
                  variants={itemVariants(reduce)}
                  className="mx-auto mt-10 max-w-[400px] text-[12.5px] leading-relaxed text-text-faint"
                >
                  {almost
                    ? 'Sıra size geliyor — sayfayı açık tutun, birazdan otomatik olarak alınacaksınız.'
                    : 'Sayfayı kapatmayın — sıranız korunuyor. Sıra size geldiğinde otomatik olarak koltuk seçimine yönlendirileceksiniz.'}
                </motion.p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </main>

      {/* Live region for assistive tech — announces position quietly */}
      <p className="sr-only" aria-live="polite">
        {connecting
          ? 'Sıraya bağlanılıyor'
          : admitted
            ? 'Sıra sizde, yönlendiriliyorsunuz'
            : `Sıradaki yeriniz ${q.position}, önünüzde ${q.peopleAhead} kişi`}
      </p>
    </div>
  );
}

// Route-ready wrapper: admits the visitor into the booking page. In production
// the gate decides when to mount the waiting room; here it stands alone so the
// flow is previewable at /bekleme.
export function WaitingRoomRoute() {
  const navigate = useNavigate();
  return <WaitingRoom onAdmit={() => navigate('/etkinlik')} />;
}

function itemVariants(reduce: boolean | null) {
  if (reduce) return undefined;
  return {
    hidden: { opacity: 0, y: 14 },
    show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 90, damping: 18 } },
  };
}

function LivePulse({ reduce, dim }: { reduce: boolean; dim: boolean }) {
  return (
    <span className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-text-faint">
      <span className="relative flex h-1.5 w-1.5">
        {!reduce && !dim && (
          <motion.span
            className="absolute inline-flex h-full w-full rounded-full"
            style={{ background: 'var(--accent)' }}
            animate={{ scale: [1, 2.6], opacity: [0.5, 0] }}
            transition={{ duration: 1.8, repeat: Number.POSITIVE_INFINITY, ease: 'easeOut' }}
          />
        )}
        <span
          className="relative inline-flex h-1.5 w-1.5 rounded-full"
          style={{ background: dim ? 'var(--text-faint)' : 'var(--accent)' }}
        />
      </span>
      {dim ? 'Bağlanıyor' : 'Canlı'}
    </span>
  );
}

function ProgressRule({
  progress,
  reduce,
  active,
}: {
  progress: number;
  reduce: boolean;
  active: boolean;
}) {
  const pct = `${Math.min(100, Math.max(0, progress * 100))}%`;
  return (
    <div className="mx-auto h-px w-full max-w-[440px] overflow-hidden bg-border-strong">
      <motion.div
        className="h-full"
        style={{ background: 'var(--accent)', opacity: active ? 1 : 0.7 }}
        initial={false}
        animate={{ width: pct }}
        transition={reduce ? { duration: 0 } : { type: 'spring', stiffness: 40, damping: 22 }}
      />
    </div>
  );
}
